/* P.Louise — Video Reveal Popup
   Opens a whole-page video popup on load. The Reveal button (or the end of
   the video) snapshots the current frame onto two half-width doors and
   swings them open to reveal the page beneath.

   Behaviour notes:
   - Desktop and mobile are independent: each has its own on/off switch and
     its own media. The script keeps only the current viewport's media.
   - Frequency ("always" / "session" / "once") is remembered per section.
   - In the theme editor the popup opens only when the section is selected.
   - Reduced-motion users get a plain fade instead of the door animation.   */

(function () {
  'use strict';

  /* The section may be placed more than once; only one copy of the script
     should bind document-level listeners. */
  if (window.__plVideoRevealPopup) return;
  window.__plVideoRevealPopup = true;

  var REDUCED_MOTION = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var MOBILE_QUERY = '(max-width: 749px)';
  var FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function init(root) {
    if (!root || root.getAttribute('data-vrp-init') === 'true') return;
    root.setAttribute('data-vrp-init', 'true');

    var sectionId  = root.getAttribute('data-section-id') || '';
    var frequency  = root.getAttribute('data-frequency') || 'session';
    var delay      = parseFloat(root.getAttribute('data-delay')) || 0;
    var autoOpen   = root.getAttribute('data-auto-open') === 'true';
    var duration   = parseFloat(root.getAttribute('data-duration')) || 1.2;
    var designMode = root.getAttribute('data-design-mode') === 'true' || !!(window.Shopify && window.Shopify.designMode);
    var showDesktop = root.getAttribute('data-show-desktop') !== 'false';
    var showMobile  = root.getAttribute('data-show-mobile') !== 'false';
    var storageKey = 'vrp:' + sectionId;
    var mobileMq   = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
    var isMobile   = !!(mobileMq && mobileMq.matches);

    var screen = root.querySelector('[data-vrp-screen]');
    var doors  = root.querySelector('[data-vrp-doors]');
    var doorEls = Array.prototype.slice.call(root.querySelectorAll('[data-vrp-door]'));
    var cta    = root.querySelector('[data-vrp-reveal]');
    var sound  = root.querySelector('[data-vrp-sound]');
    var video  = selectMedia(root, isMobile);

    /* Is the popup switched on for the viewport we're in? */
    function enabledHere() { return isMobile ? showMobile : showDesktop; }

    var state = {
      open: false,
      revealing: false,
      revealed: false,
      openTimer: null,
      finishTimer: null,
      prevFocus: null
    };

    if (REDUCED_MOTION) {
      root.classList.add('vrp--reduced');
      duration = 0.45;
    }

    /* ---------- Storage (frequency) ---------- */
    function store() {
      try {
        return frequency === 'once' ? window.localStorage : window.sessionStorage;
      } catch (e) { return null; }
    }
    function hasSeen() {
      if (designMode || frequency === 'always') return false;
      try { var s = store(); return !!(s && s.getItem(storageKey) === '1'); } catch (e) { return false; }
    }
    function markSeen() {
      if (designMode || frequency === 'always') return;
      try { var s = store(); if (s) s.setItem(storageKey, '1'); } catch (e) {}
    }

    /* ---------- Scroll lock ---------- */
    function lockScroll()   { document.documentElement.classList.add('vrp-lock'); }
    function unlockScroll() { document.documentElement.classList.remove('vrp-lock'); }

    /* ---------- Video helpers ---------- */
    function tryPlay() {
      if (!video) return;
      var p;
      try { p = video.play(); } catch (e) { return; }
      if (p && typeof p.catch === 'function') p.catch(function () { /* autoplay blocked: poster + button remain */ });
    }

    function setSound(on) {
      if (!video || !sound) return;
      video.muted = !on;
      sound.classList.toggle('is-on', on);
      sound.setAttribute('aria-pressed', on ? 'true' : 'false');
      sound.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
      if (on && video.paused && !state.revealing && !state.revealed) tryPlay();
    }

    /* Draw the current video frame onto each door's canvas so the doors carry
       the exact picture on screen at the moment of the reveal. Falls back to
       the poster (already in the door markup) if the frame can't be drawn. */
    function snapshot() {
      if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return false;

      var W = root.clientWidth, H = root.clientHeight;
      if (!W || !H) return false;

      var vw = video.videoWidth, vh = video.videoHeight;
      var scale = Math.max(W / vw, H / vh);          /* object-fit: cover */
      var dw = vw * scale, dh = vh * scale;
      var dx = (W - dw) / 2, dy = (H - dh) / 2;      /* object-position: center */
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var drawn = false;

      doorEls.forEach(function (door) {
        var canvas = door.querySelector('[data-vrp-canvas]');
        if (!canvas) return;
        var rect = door.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        try {
          canvas.width  = Math.round(rect.width * dpr);
          canvas.height = Math.round(rect.height * dpr);
          var ctx = canvas.getContext('2d');
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.drawImage(video, dx - rect.left, dy - rect.top, dw, dh);
          door.classList.add('has-frame');
          drawn = true;
        } catch (e) {
          door.classList.remove('has-frame');
        }
      });

      return drawn;
    }

    /* ---------- Open / reveal / finish ---------- */
    function reset() {
      clearTimeout(state.finishTimer);
      state.revealing = false;
      state.revealed = false;
      root.classList.remove('is-open', 'is-revealing');
      if (doors) { doors.classList.remove('is-ready'); doors.setAttribute('aria-hidden', 'true'); }
      if (screen) screen.classList.remove('is-hidden');
      doorEls.forEach(function (d) { d.classList.remove('has-frame'); });
    }

    function open() {
      if (state.open || !enabledHere()) return;
      reset();
      state.open = true;
      state.prevFocus = document.activeElement;

      root.classList.add('is-visible');
      lockScroll();
      markSeen();

      if (video) {
        try { video.currentTime = 0; } catch (e) {}
        tryPlay();
      }

      setTimeout(function () {
        if (cta && state.open) {
          try { cta.focus({ preventScroll: true }); } catch (e) { cta.focus(); }
        }
      }, 60);
    }

    function reveal() {
      if (!state.open || state.revealing || state.revealed) return;
      state.revealing = true;
      root.classList.add('is-revealing');

      /* The doors must be laid out before the frame can be drawn onto them,
         and they must be showing before the live video is hidden so nothing
         behind the popup flashes through. */
      if (doors) { doors.classList.add('is-ready'); doors.setAttribute('aria-hidden', 'false'); }
      void root.offsetWidth;
      snapshot();
      if (video) { try { video.pause(); } catch (e) {} }

      requestAnimationFrame(function () {
        if (screen) screen.classList.add('is-hidden');
        requestAnimationFrame(function () {
          root.classList.add('is-open');
        });
      });

      clearTimeout(state.finishTimer);
      state.finishTimer = setTimeout(finish, duration * 1000 + 120);
    }

    function finish() {
      state.revealed = true;
      state.revealing = false;
      state.open = false;
      closeNow();

      var evt;
      try { evt = new CustomEvent('vrp:revealed', { bubbles: true, detail: { sectionId: sectionId } }); }
      catch (e) { evt = document.createEvent('Event'); evt.initEvent('vrp:revealed', true, true); }
      root.dispatchEvent(evt);
    }

    /* Hide immediately without the door animation. */
    function closeNow() {
      clearTimeout(state.openTimer);
      clearTimeout(state.finishTimer);
      state.open = false;
      root.classList.remove('is-visible', 'is-open', 'is-revealing');
      unlockScroll();
      if (video) { try { video.pause(); } catch (e) {} }

      var prev = state.prevFocus;
      state.prevFocus = null;
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        try { prev.focus({ preventScroll: true }); } catch (e) {}
      }
    }

    /* ---------- Events ---------- */
    if (cta) cta.addEventListener('click', reveal);

    if (sound && video) {
      sound.addEventListener('click', function () { setSound(video.muted); });
    }

    if (video) {
      video.addEventListener('ended', function () {
        if (autoOpen) reveal();
      });

      /* A video that can't load shouldn't trap the visitor behind the popup. */
      var onError = function () { if (state.open && autoOpen) reveal(); };
      video.addEventListener('error', onError);
      var sources = video.querySelectorAll('source');
      if (sources.length) sources[sources.length - 1].addEventListener('error', onError);
    }

    root.addEventListener('keydown', function (e) {
      if (!state.open) return;

      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        reveal();
        return;
      }

      /* Keep keyboard focus inside the dialog. */
      if (e.key === 'Tab') {
        var items = Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE)).filter(function (el) {
          return el.offsetParent !== null;
        });
        if (!items.length) return;
        var first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    document.addEventListener('keydown', function (e) {
      if (state.open && (e.key === 'Escape' || e.key === 'Esc') && !root.contains(document.activeElement)) {
        e.preventDefault();
        reveal();
      }
    });

    /* If the window is resized across the breakpoint into a device that's
       switched off, take the popup down. */
    if (mobileMq) {
      var onMqChange = function (e) {
        isMobile = !!e.matches;
        if (state.open && !enabledHere()) closeNow();
      };
      if (typeof mobileMq.addEventListener === 'function') mobileMq.addEventListener('change', onMqChange);
      else if (typeof mobileMq.addListener === 'function') mobileMq.addListener(onMqChange);
    }

    /* ---------- Theme editor ---------- */
    if (designMode) {
      document.addEventListener('shopify:section:select', function (e) {
        if (e.detail && e.detail.sectionId === sectionId) { closeNow(); open(); }
      });
      document.addEventListener('shopify:section:deselect', function (e) {
        if (e.detail && e.detail.sectionId === sectionId) closeNow();
      });
    }

    /* ---------- Kick off ---------- */
    if (!designMode && enabledHere() && !hasSeen()) {
      state.openTimer = setTimeout(open, delay * 1000);
    }
  }

  /* Keep only the media the current viewport needs and drop the rest so it
     never downloads. Mobile prefers its own video/poster and falls back to
     the desktop ones; desktop uses the desktop ones only. Returns the video
     element to drive, or null when the device runs on a poster alone. */
  function selectMedia(root, isMobile) {
    var pick = function (base) {
      var d = root.querySelector('[' + base + '-desktop]');
      var m = root.querySelector('[' + base + '-mobile]');
      if (isMobile) return m || d;
      return d;
    };
    var remove = function (el) { if (el && el.parentNode) el.parentNode.removeChild(el); };
    var show = function (el) {
      if (!el) return;
      el.style.display = 'block';
      if (el.getAttribute('loading') === 'lazy') el.setAttribute('loading', 'eager');
    };

    var video  = pick('data-vrp-video');
    var poster = video ? null : pick('data-vrp-poster');

    Array.prototype.forEach.call(root.querySelectorAll('[data-vrp-video]'), function (el) { if (el !== video) remove(el); });
    Array.prototype.forEach.call(root.querySelectorAll('[data-vrp-poster-desktop], [data-vrp-poster-mobile]'), function (el) { if (el !== poster) remove(el); });

    if (video) { video.preload = 'auto'; show(video); }
    show(poster);

    /* Door fallback images, one per door. */
    Array.prototype.forEach.call(root.querySelectorAll('[data-vrp-door]'), function (door) {
      var d = door.querySelector('[data-vrp-door-img-desktop]');
      var m = door.querySelector('[data-vrp-door-img-mobile]');
      var keep = isMobile ? (m || d) : d;
      if (d && d !== keep) remove(d);
      if (m && m !== keep) remove(m);
      show(keep);
    });

    return video;
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-vrp]');
    Array.prototype.forEach.call(nodes, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });
})();